// Compliance view for document management and requirements

import React, { useState } from 'react'
import { useEventCompliance } from '../../hooks/useSupplierTasks'
import styles from './ComplianceView.module.css'

interface ComplianceViewProps {
  eventId: string
}

interface ComplianceRequirement {
  id: string
  title: string
  description: string
  type: 'document' | 'certificate' | 'license' | 'insurance'
  required: boolean
  dueDate?: string
  status: 'pending' | 'submitted' | 'approved' | 'rejected'
  uploadedDocument?: {
    filename: string
    uploadDate: string
    fileSize: number
  }
  rejectionReason?: string
}

interface ComplianceInfo {
  requirements: ComplianceRequirement[]
  generalInstructions?: string[]
  contactInfo?: {
    name: string
    role: string
    email: string
    phone?: string
  }
}

export const ComplianceView: React.FC<ComplianceViewProps> = ({ eventId }) => {
  const { data: compliance, isLoading, error } = useEventCompliance(eventId)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'completed'>('all')

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading compliance requirements...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h3>Unable to Load Compliance</h3>
        <p>We couldn't retrieve compliance requirements for this event.</p>
        <p>Please contact your event coordinator for details.</p>
      </div>
    )
  }

  if (!compliance) {
    return (
      <div className={styles.emptyContainer}>
        <div className={styles.emptyIcon}>📄</div>
        <h3>No Compliance Requirements</h3>
        <p>There are no compliance requirements for this event.</p>
      </div>
    )
  }

  const complianceData = compliance as ComplianceInfo
  const requirements = complianceData.requirements || []

  // Filter requirements based on active filter
  const filteredRequirements = requirements.filter(req => {
    if (activeFilter === 'all') return true
    if (activeFilter === 'pending') return req.status === 'pending' || req.status === 'rejected'
    if (activeFilter === 'completed') return req.status === 'submitted' || req.status === 'approved'
    return true
  })

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, requirementId: string) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB')
        return
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
      if (!allowedTypes.includes(file.type)) {
        alert('Only PDF, JPG, and PNG files are allowed')
        return
      }

      handleFileUpload(file, requirementId)
    }
  }

  const handleFileUpload = async (file: File, requirementId: string) => {
    setUploading(requirementId)
    
    try {
      // This would normally be an API call to upload the file
      // For now, we'll simulate the upload
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Update the requirement status (in real app, this would come from the API response)
      alert(`File "${file.name}" uploaded successfully!`)
      
    } catch (error) {
      console.error('Upload failed:', error)
      alert('Upload failed. Please try again.')
    } finally {
      setUploading(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return '✅'
      case 'submitted':
        return '📤'
      case 'rejected':
        return '❌'
      default:
        return '⏳'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approved'
      case 'submitted':
        return 'Under Review'
      case 'rejected':
        return 'Rejected'
      default:
        return 'Pending'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'certificate':
        return '🏆'
      case 'license':
        return '📜'
      case 'insurance':
        return '🛡️'
      default:
        return '📄'
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const diffTime = date.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`
    } else if (diffDays === 0) {
      return 'Due today'
    } else if (diffDays <= 7) {
      return `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      })
    }
  }

  const pendingCount = requirements.filter(r => r.status === 'pending' || r.status === 'rejected').length
  const completedCount = requirements.filter(r => r.status === 'submitted' || r.status === 'approved').length

  return (
    <div className={styles.container}>
      {/* Header with summary stats */}
      <div className={styles.header}>
        <div className={styles.summaryStats}>
          <div className={styles.stat}>
            <div className={styles.statValue}>{requirements.length}</div>
            <div className={styles.statLabel}>Total Requirements</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{completedCount}</div>
            <div className={styles.statLabel}>Completed</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{pendingCount}</div>
            <div className={styles.statLabel}>Pending</div>
          </div>
        </div>

        {/* Filter buttons */}
        <div className={styles.filters}>
          <button
            className={`${styles.filterButton} ${activeFilter === 'all' ? styles.active : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All ({requirements.length})
          </button>
          <button
            className={`${styles.filterButton} ${activeFilter === 'pending' ? styles.active : ''}`}
            onClick={() => setActiveFilter('pending')}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={`${styles.filterButton} ${activeFilter === 'completed' ? styles.active : ''}`}
            onClick={() => setActiveFilter('completed')}
          >
            Completed ({completedCount})
          </button>
        </div>
      </div>

      {/* General instructions */}
      {complianceData.generalInstructions && complianceData.generalInstructions.length > 0 && (
        <div className={styles.instructionsCard}>
          <h3 className={styles.cardTitle}>
            <span className={styles.cardIcon}>ℹ️</span>
            General Instructions
          </h3>
          <div className={styles.cardContent}>
            <ul className={styles.instructionsList}>
              {complianceData.generalInstructions.map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Requirements list */}
      <div className={styles.requirementsList}>
        {filteredRequirements.length === 0 ? (
          <div className={styles.emptyFilter}>
            <div className={styles.emptyFilterIcon}>🔍</div>
            <h3>No Requirements Found</h3>
            <p>No requirements match your current filter.</p>
          </div>
        ) : (
          filteredRequirements.map((requirement) => {
            const isOverdue = requirement.dueDate && new Date(requirement.dueDate) < new Date()
            const isDueSoon = requirement.dueDate && new Date(requirement.dueDate).getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000

            return (
              <div 
                key={requirement.id} 
                className={`
                  ${styles.requirementCard}
                  ${requirement.status === 'approved' ? styles.approved : ''}
                  ${requirement.status === 'rejected' ? styles.rejected : ''}
                  ${isOverdue ? styles.overdue : ''}
                  ${isDueSoon && requirement.status === 'pending' ? styles.dueSoon : ''}
                `.trim()}
              >
                <div className={styles.requirementHeader}>
                  <div className={styles.requirementTitle}>
                    <span className={styles.typeIcon}>{getTypeIcon(requirement.type)}</span>
                    <div>
                      <h4>{requirement.title}</h4>
                      {requirement.required && (
                        <span className={styles.requiredBadge}>Required</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.requirementStatus}>
                    <span className={styles.statusIcon}>{getStatusIcon(requirement.status)}</span>
                    <span className={styles.statusText}>{getStatusText(requirement.status)}</span>
                  </div>
                </div>

                <p className={styles.requirementDescription}>{requirement.description}</p>

                {requirement.dueDate && (
                  <div className={`${styles.dueDate} ${isOverdue ? styles.overdueDue : ''}`}>
                    <span className={styles.clockIcon}>⏰</span>
                    {formatDate(requirement.dueDate)}
                  </div>
                )}

                {/* Upload section */}
                {(requirement.status === 'pending' || requirement.status === 'rejected') && (
                  <div className={styles.uploadSection}>
                    <input
                      type="file"
                      id={`file-${requirement.id}`}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileSelect(e, requirement.id)}
                      className={styles.fileInput}
                      disabled={uploading === requirement.id}
                    />
                    <label htmlFor={`file-${requirement.id}`} className={styles.uploadButton}>
                      {uploading === requirement.id ? (
                        <>
                          <span className={styles.uploadSpinner}></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <span className={styles.uploadIcon}>📤</span>
                          {requirement.uploadedDocument ? 'Replace Document' : 'Upload Document'}
                        </>
                      )}
                    </label>
                    <div className={styles.uploadHint}>
                      PDF, JPG, PNG files only. Max 10MB.
                    </div>
                  </div>
                )}

                {/* Uploaded document info */}
                {requirement.uploadedDocument && (
                  <div className={styles.uploadedDocument}>
                    <div className={styles.documentInfo}>
                      <span className={styles.documentIcon}>📎</span>
                      <div>
                        <div className={styles.documentName}>{requirement.uploadedDocument.filename}</div>
                        <div className={styles.documentMeta}>
                          {formatFileSize(requirement.uploadedDocument.fileSize)} • 
                          Uploaded {new Date(requirement.uploadedDocument.uploadDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rejection reason */}
                {requirement.status === 'rejected' && requirement.rejectionReason && (
                  <div className={styles.rejectionReason}>
                    <span className={styles.rejectionIcon}>❌</span>
                    <div>
                      <strong>Rejection Reason:</strong>
                      <p>{requirement.rejectionReason}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Contact info */}
      {complianceData.contactInfo && (
        <div className={styles.contactCard}>
          <h3 className={styles.cardTitle}>
            <span className={styles.cardIcon}>📞</span>
            Compliance Contact
          </h3>
          <div className={styles.cardContent}>
            <div className={styles.contactInfo}>
              <strong>{complianceData.contactInfo.name}</strong>
              <span className={styles.contactRole}>({complianceData.contactInfo.role})</span>
            </div>
            <div className={styles.contactDetails}>
              <a href={`mailto:${complianceData.contactInfo.email}`} className={styles.emailLink}>
                {complianceData.contactInfo.email}
              </a>
              {complianceData.contactInfo.phone && (
                <a href={`tel:${complianceData.contactInfo.phone}`} className={styles.phoneLink}>
                  {complianceData.contactInfo.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}